import { Button, DatePicker, Empty, Input, Select, Table } from 'antd'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import FadeIn from 'src/components/styled/FadeIn'
import { API } from 'src/utils/interceptor'
import { canManageAnyVenue } from 'src/utils/adminAccess'
import {
  formatGroupGp,
  formatGroupSales,
  formatUkSalesRange,
  GROUP_MEMBERS_CAPTION,
  GROUP_SALES_PERIOD_OPTIONS,
  groupSalesFigures,
  groupSalesPeriodSelectValue,
  loadGroupSalesInOrder,
  pastThirtyDayRange,
  readStoredGroupSalesPeriod,
  resolveGroupSalesRange,
  salesFailedLine,
  writeStoredGroupSalesPeriod,
} from 'src/screens/venue-groups/VenueGroupPage'

import { VENUE_GROUP_ACTION_LABELS } from 'src/screens/venue-groups/bulkActionConfirmation'
import {
  GroupHero,
  GroupLead,
  GroupPage,
  GroupSurface,
  GroupTable,
  GroupTitle,
} from 'src/screens/venue-groups/venueGroupChrome'

export const formatVenueCount = count => {
  const total = Number(count) || 0
  if (total === 1) return '1 venue'
  return `${total} venues`
}

/** List intro: only manageable venues are shown, even in a shared group. */
export const GROUPS_LIST_LEAD =
  'Only venues you can manage show here. Other sites in a shared group stay out of your view.'

/** Quiet list badge for support batches hidden from customers. */
export const GROUPS_OMS_ONLY_BADGE = 'OMS only'

/** Empty list with an estate: point at Create and Venues → Add to group. */
export const GROUPS_EMPTY_MANY_VENUE_MIN = 2

export const GROUPS_EMPTY_ESTATE_HINT =
  'Create a group, or pick sites on Venues → Add to group'

/** How many venues this account likely manages (for the empty-state hint). */
export const accountVenueCountForGroupsEmpty = account => {
  if (!account) return 0
  if (
    account.isAdmin === true ||
    account.TFRAdmin ||
    account.GREENAdmin ||
    account.JUSTAdmin
  ) {
    return GROUPS_EMPTY_MANY_VENUE_MIN
  }
  return Math.max(
    (account.venues || []).length,
    Object.keys(account.venueRoles || {}).length,
  )
}

export const groupsEmptyShowsEstateHint = account =>
  accountVenueCountForGroupsEmpty(account) >= GROUPS_EMPTY_MANY_VENUE_MIN

export const filterGroups = (groups, query) => {
  const needle = String(query || '')
    .trim()
    .toLowerCase()
  if (!needle) return groups || []
  return (groups || []).filter(group => {
    const name = String(group?.name || '').toLowerCase()
    const venues = (group?.venueNames || []).join(' ').toLowerCase()
    return name.includes(needle) || venues.includes(needle)
  })
}

export const GROUPS_LIST_SKELETON_COUNT = 3

export const groupsListSkeletonRows = (count = GROUPS_LIST_SKELETON_COUNT) =>
  Array.from({ length: count }, (_, index) => ({
    _id: `groups-skeleton-${index}`,
    name: '',
    venueCount: null,
    lastRun: null,
    skeleton: true,
  }))

/** Keep previous rows while refetching; only invent skeleton rows on first load. */
export const groupsListDataSource = ({ loading, rows } = {}) => {
  const list = rows || []
  if (loading && list.length === 0) return groupsListSkeletonRows()
  return list
}

export const formatGroupLastRun = run => {
  if (!run) return 'No runs yet'
  const action = VENUE_GROUP_ACTION_LABELS[run.action] || 'Run'
  const when = run.createdAt
    ? new Date(run.createdAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : ''
  const result = run.refreshOnly
    ? 'Already on the other venues. Sharing again only refreshes the multiplier, VAT, and order'
    : Number.isInteger(run.updated)
    ? `${run.updated} updated`
    : ''
  return [action, result, when].filter(Boolean).join(' · ')
}

export const formatLastRun = run => {
  if (!run) return 'No runs yet'
  const action = VENUE_GROUP_ACTION_LABELS[run.action] || 'Run'
  const when = run.createdAt
    ? new Date(run.createdAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : ''
  const status =
    run.status === 'finished'
      ? 'finished'
      : run.status === 'running'
      ? 'in progress'
      : 'queued'
  const result =
    run.status === 'finished' && Number.isInteger(run.updated)
      ? `${run.updated} updated`
      : ''
  return [action, status, result, when].filter(Boolean).join(' · ')
}

/** List row venue ids — only what the filtered list payload returned. */
export const venueIdsFromGroupListRow = group =>
  (group?.venues || []).map(id => String(id?._id || id))

export const groupListSalesCell = (
  group,
  rowsById,
  { loading = false, missed = [] } = {},
) => {
  const ids = venueIdsFromGroupListRow(group)
  if (!ids.length) return { figures: 'No sales', note: '' }
  const missedIds = missed.map(String)
  const failedHere = ids.filter(id => missedIds.includes(id))
  const rows = ids.map(id => rowsById?.[id]).filter(Boolean)
  const pending = ids.filter(id => !rowsById?.[id] && !missedIds.includes(id))
  if (loading && pending.length)
    return { figures: `${rows.length} of ${ids.length}`, note: '' }
  const named = ids.map((id, index) => ({
    _id: id,
    title:
      group?.venueNames?.[index] ||
      group?.venues?.find(venue => String(venue?._id) === id)?.title,
  }))
  const failedNote = salesFailedLine(failedHere, named)
  if (!rows.length)
    return { figures: failedNote ? '' : 'No sales', note: failedNote }
  const summary = groupSalesFigures({ data: rows }, ids.length)
  const share = `from ${summary.withSales} of ${summary.venues} venues`
  const figures = !summary.sales
    ? `No sales · ${share}`
    : [formatGroupSales(summary.sales), formatGroupGp(summary.gpp), share]
        .filter(Boolean)
        .join(' · ')
  return { figures, note: failedNote }
}

const VenueGroups = () => {
  const history = useHistory()
  const account = useSelector(state => state.auth.account)
  const [groups, setGroups] = useState([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [salesRows, setSalesRows] = useState({})
  const [salesLoading, setSalesLoading] = useState(false)
  const [salesMissed, setSalesMissed] = useState([])
  const [salesRange, setSalesRange] = useState(null)
  const [salesPeriod, setSalesPeriod] = useState(readStoredGroupSalesPeriod)

  const chooseSalesPeriod = next => {
    writeStoredGroupSalesPeriod(next)
    setSalesPeriod(next)
  }

  useEffect(() => {
    if (!canManageAnyVenue(account)) {
      history.replace('/venues')
      return undefined
    }
    let cancelled = false
    setLoading(true)
    const groupsRequest = API.get('api2', 'venue-groups')
      .then(response => {
        if (!cancelled) setGroups(response?.data || [])
        return null
      })
      .catch(() => {
        // Keep previous rows on a failed refresh; only clear when we had none.
        if (!cancelled) {
          setGroups(current => (current.length ? current : []))
        }
        return null
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    groupsRequest.catch(() => null)
    return () => {
      cancelled = true
    }
  }, [account, history])

  useEffect(() => {
    const ids = [
      ...new Set(groups.flatMap(group => venueIdsFromGroupListRow(group))),
    ]
    if (!ids.length) {
      setSalesRows({})
      setSalesMissed([])
      setSalesLoading(false)
      return undefined
    }
    let cancelled = false
    const range = resolveGroupSalesRange(salesPeriod)
    const rows = {}
    setSalesRange(range)
    setSalesRows({})
    setSalesMissed([])
    setSalesLoading(true)
    loadGroupSalesInOrder(ids, id =>
      API.post('api2', 'gross-profit/get-sales-data-for-venues', {
        body: { venues: [id], fromDate: range.fromDate, toDate: range.toDate },
      }).then(body => {
        const next = Array.isArray(body) ? body : body?.data || []
        next.forEach(row => {
          rows[String(row?.venueTitle?._id || id)] = row
        })
        if (!next.length)
          rows[String(id)] = { venueTitle: { _id: id }, totalSaleAmount: 0 }
        if (!cancelled) setSalesRows({ ...rows })
        return body
      }),
    )
      .then(result => {
        if (!cancelled) setSalesMissed(result?.failed || [])
        return null
      })
      .finally(() => {
        if (!cancelled) setSalesLoading(false)
      })
      .catch(() => null)
    return () => {
      cancelled = true
    }
  }, [groups, salesPeriod])

  if (!canManageAnyVenue(account)) return null

  const shown = filterGroups(groups, query)
  const listRows = groupsListDataSource({ loading, rows: shown })
  const emptyEstateHint = groupsEmptyShowsEstateHint(account)

  return (
    <FadeIn>
      <GroupPage $wide>
        <GroupHero>
          <HeaderRow>
            <div>
              <GroupTitle>Groups</GroupTitle>
              <GroupLead>
                Group venues together to change them in one go, and see sales
                and GP for the group. {GROUPS_LIST_LEAD}
              </GroupLead>
            </div>
            <Button
              type='primary'
              onClick={() => history.push('/venue-groups/new')}
            >
              Create group
            </Button>
          </HeaderRow>
        </GroupHero>
        {!loading && groups.length === 0 ? (
          <GroupSurface>
            <Empty
              description={
                emptyEstateHint ? (
                  <EmptyEstateHint>
                    <Link to='/venue-groups/new'>Create a group</Link>, or pick
                    sites on <Link to='/venues'>Venues</Link>
                    {' → Add to group'}
                  </EmptyEstateHint>
                ) : (
                  'No groups yet'
                )
              }
            >
              <Button
                type='primary'
                onClick={() => history.push('/venue-groups/new')}
              >
                Create group
              </Button>
            </Empty>
          </GroupSurface>
        ) : (
          <GroupSurface>
            <SearchInput
              allowClear
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder='Search groups or venues'
            />
            <PeriodRow>
              <span>Sales period</span>
              <Select
                value={groupSalesPeriodSelectValue(salesPeriod)}
                onChange={value => {
                  if (value === 'custom') {
                    chooseSalesPeriod({
                      id: 'custom',
                      fromDate:
                        salesPeriod?.fromDate ||
                        salesRange?.fromDate ||
                        pastThirtyDayRange().fromDate,
                      toDate:
                        salesPeriod?.toDate ||
                        salesRange?.toDate ||
                        pastThirtyDayRange().toDate,
                    })
                    return
                  }
                  chooseSalesPeriod({ id: value })
                }}
                style={{ width: 160 }}
              >
                {GROUP_SALES_PERIOD_OPTIONS.map(option => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.label}
                  </Select.Option>
                ))}
                <Select.Option value='custom'>Custom dates</Select.Option>
              </Select>
              {salesPeriod?.id === 'custom' ? (
                <DatePicker.RangePicker
                  allowClear={false}
                  value={[
                    salesPeriod.fromDate ? moment(salesPeriod.fromDate) : null,
                    salesPeriod.toDate ? moment(salesPeriod.toDate) : null,
                  ]}
                  onChange={dates => {
                    if (!dates?.[0] || !dates?.[1]) return
                    chooseSalesPeriod({
                      id: 'custom',
                      fromDate: dates[0].format('YYYY-MM-DD'),
                      toDate: dates[1].format('YYYY-MM-DD'),
                    })
                  }}
                />
              ) : null}
            </PeriodRow>
            {!loading && shown.length === 0 ? (
              <Empty description='No groups match' />
            ) : (
              <ListTable>
                <Table
                  rowKey='_id'
                  loading={loading}
                  dataSource={listRows}
                  pagination={false}
                  locale={{ emptyText: ' ' }}
                  onRow={group =>
                    group.skeleton
                      ? { className: 'group-list-row group-list-skeleton' }
                      : {
                          onClick: () =>
                            history.push(`/venue-groups/${group._id}`),
                          className: 'group-list-row',
                        }
                  }
                  columns={[
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      width: '28%',
                      render: (name, group) =>
                        group.skeleton ? (
                          <SkeletonBar $width='56%' />
                        ) : (
                          <Link
                            className='group-name'
                            to={`/venue-groups/${group._id}`}
                            onClick={event => event.stopPropagation()}
                          >
                            <span className='group-name-text'>{name}</span>
                            {group.customerVisible === false ? (
                              <span
                                className='group-oms-only'
                                title='Hidden from customers'
                              >
                                {GROUPS_OMS_ONLY_BADGE}
                              </span>
                            ) : null}
                          </Link>
                        ),
                    },
                    {
                      title: GROUP_MEMBERS_CAPTION,
                      dataIndex: 'venueCount',
                      render: (count, group) =>
                        group.skeleton ? (
                          <SkeletonBar $width='4.5rem' />
                        ) : (
                          <span className='group-venues'>
                            {formatVenueCount(count)}
                          </span>
                        ),
                    },
                    {
                      title: salesRange
                        ? formatUkSalesRange(
                            salesRange.fromDate,
                            salesRange.toDate,
                            { withHint: false },
                          )
                        : 'Past 30 days',
                      key: 'sales',
                      width: '24%',
                      render: (_, group) => {
                        if (group.skeleton) {
                          return <SkeletonBar $width='70%' />
                        }
                        const sales = groupListSalesCell(group, salesRows, {
                          loading: salesLoading,
                          missed: salesMissed,
                        })
                        return (
                          <span className='group-sales'>
                            {sales.figures ? (
                              <span>{sales.figures}</span>
                            ) : null}
                            {sales.note ? (
                              <span className='group-sales-missed'>
                                {sales.note}
                              </span>
                            ) : null}
                          </span>
                        )
                      },
                    },
                    {
                      title: 'Last run',
                      dataIndex: 'lastRun',
                      width: '32%',
                      render: (run, group) =>
                        group.skeleton ? (
                          <SkeletonBar $width='80%' />
                        ) : (
                          <span className='group-run'>
                            {formatLastRun(run)}
                          </span>
                        ),
                    },
                  ]}
                />
              </ListTable>
            )}
          </GroupSurface>
        )}
      </GroupPage>
    </FadeIn>
  )
}

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`

const EmptyEstateHint = styled.span`
  color: #5c6560;
  font-size: 14px;
  line-height: 1.45;

  a {
    color: #132d07;
    font-weight: 600;
    text-decoration: underline;
  }
`

const SearchInput = styled(Input)`
  width: 100%;
  margin-bottom: 16px;
`

const PeriodRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  color: #5c6560;
  font-size: 13px;
`

const SkeletonBar = styled.span`
  display: inline-block;
  height: 12px;
  width: ${props => props.$width || '40%'};
  max-width: 100%;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef3e4 0%, #f7fbec 50%, #eef3e4 100%);
  background-size: 200% 100%;
  animation: groupsListPulse 1.2s ease-in-out infinite;

  @keyframes groupsListPulse {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -100% 0;
    }
  }
`

const ListTable = styled(GroupTable)`
  .group-venues,
  .group-run,
  .group-sales {
    display: block;
    white-space: nowrap;
  }

  .ant-table-tbody > tr.group-list-skeleton {
    cursor: default;
    pointer-events: none;
  }

  a.group-name {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    color: inherit;
    white-space: nowrap;
  }

  .group-name-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .group-oms-only {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 999px;
    border: 1px solid #d5ddd0;
    background: #f4f6f2;
    color: #5c6560;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.35;
    letter-spacing: 0.01em;
  }

  .group-sales-missed {
    display: block;
    margin-top: 2px;
    color: #5c6560;
    font-weight: 500;
    white-space: normal;
  }

  .ant-table-tbody > tr.group-list-row {
    cursor: pointer;
  }

  .ant-table-tbody > tr.group-list-row:hover > td {
    background: #f3f8e4 !important;
  }

  .ant-table-tbody > tr.group-list-row:hover .group-name {
    text-decoration: underline;
  }
`

export default VenueGroups
